<template>
	<UIShButton
		kind="unstyled"
		:label
		:icon-left
		:icon-right
		:class="btnClasses"
		@click="$emit('click')"
	>
		<template v-for="(_, name) in $slots" #[name]="slotProps">
			<slot :name="name" v-bind="slotProps || {}"></slot>
		</template>
	</UIShButton>
</template>

<script setup lang="ts">
const SIZES = ['sm', 'md', 'lg'] as const;

type SizesT = 'sm' | 'md' | 'lg';
type SizeName = `btn-label-${(typeof SIZES)[number]}`;
type SizeNameAndValue = `btn-label-${(typeof SIZES)[number]}${number}`;
type UISizeBckPoint = SizeName | SizeNameAndValue;

export type ShButtonLabelT = {
	label?: string;
	iconLeft?: string;
	iconRight?: string;
} & { [s in SizesT]?: number | boolean };

const $emit = defineEmits<{ (e: 'click'): void }>();
const props = defineProps<ShButtonLabelT>();

const btnClasses = computed(() => {
	const sizeClass = getSizeClass();

	return {
		'btn-label': true,
		...sizeClass
	};
});

function getSizeClass() {
	const classes: { [key in UISizeBckPoint]?: boolean } = {};

	SIZES.forEach((s) => {
		const value = props[s];

		if (!(s in props)) return;
		if (value === undefined) return;

		if (typeof value === 'boolean') {
			return (classes[`btn-label-${s}`] = value);
		}

		classes[`btn-label-${s}${value}`] = true;
	});

	return classes;
}
</script>

<style lang="scss" scoped>
.btn-label {
	padding: 0;
	width: auto;
	height: auto;
	display: flex;
	flex-wrap: nowrap;
	cursor: pointer;
	overflow: hidden;
	align-items: center;
	justify-content: center;
	color: var(--color-gray500);

	&:hover,
	&:focus,
	&:focus-visible {
		color: var(--color-brand500);
	}
}

$sizes: (
	sm: tsmb,
	md: tmdb,
	lg: tlgb
);
$screens: 768, 1024, 1440, 1920;

@mixin useBtnLabel($size, $font) {
	@include useFont($font);

	gap: var(--btn-label-#{$size}-gap);
	--icon-width: var(--btn-label-#{$size}-icon-w);
	--icon-height: var(--btn-label-#{$size}-icon-h);
}

@each $size, $font in $sizes {
	.btn-label-#{$size} {
		@include useBtnLabel($size, $font);
	}
}

@each $screen in $screens {
	@each $size, $font in $sizes {
		.btn-label-#{$size}#{$screen} {
			@media screen and (min-width: #{$screen}px) {
				@include useBtnLabel($size, $font);
			}
		}
	}
}
</style>
